import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { actions } from '../../dispatch';
import { dispatch } from '../../dispatch/dispatch';
import ContextStore from '../../hooks/ContextStore';
import { StoreFormData } from '../../interfaces/formData/StoreFormData.interface';
import { IErrorResponse } from '../../interfaces/responses/ErrorResponse.interface';
import { IStoreResponse } from '../../interfaces/responses/StoreResponse.interface';

interface IAddStoreProps {
  setShowModal: Dispatch<SetStateAction<boolean>>;
}

export const AddStoreModal: FC<IAddStoreProps> = ({ setShowModal }) => {
  const { id } = useParams();
  const { contextStore, setContextStore } = useContext(ContextStore);
  const ref = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState<StoreFormData>({
    name: '',
    client_id: '',
    refresh_token: '',
    email: '',
    password: '',
  });
  const [showForm, setShowForm] = useState<boolean>(true);

  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      if (showForm && ref.current && !ref.current.contains(e.target)) {
        setShowForm(false);
        setShowModal(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);
    return () => document.removeEventListener('mousedown', checkIfClickedOutside);
  }, [showForm]);

  const onClickSubmit = async (e: React.SyntheticEvent) => {
    contextStore.setShowSpinner(true);
    e.preventDefault();
    const storeResponse: IStoreResponse | IErrorResponse = await dispatch(
      actions.createStore, { ...formData }, contextStore.token
    );
    if ('error' in storeResponse) {
      contextStore.setShowSpinner(false);
      return console.log(storeResponse.error.message);
    }
    if (id !== '0') contextStore.socket.emit('leaveStoreLogsRoom', [id]);
    contextStore.setShowSpinner(false);
    setShowModal(false);
    navigate(`/dashboard/${storeResponse._id}`);
  };

  const onClickCancel = (e: React.SyntheticEvent) => { e.preventDefault(); setShowModal(false); };
  const onChangeFormData = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const fields = [
    { label: 'Store Name', name: 'name', type: 'text', value: formData.name, placeholder: 'My Store' },
    { label: 'Client ID', name: 'client_id', type: 'text', value: formData.client_id, placeholder: 'Jumia client ID' },
    { label: 'Refresh Token', name: 'refresh_token', type: 'text', value: formData.refresh_token, placeholder: 'OAuth refresh token' },
    { label: 'Vendor Center Email', name: 'email', type: 'email', value: formData.email, placeholder: 'vendor@example.com' },
    { label: 'Vendor Center Password', name: 'password', type: 'password', value: formData.password, placeholder: '••••••••' },
  ];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <form ref={ref} onSubmit={onClickSubmit}
        className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-base font-bold text-foreground">Add Store</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">Connect a new Jumia seller account</p>
        </div>
        <div className="px-6 py-4 space-y-3">
          {fields.map(({ label, name, type, value, placeholder }) => (
            <div key={name}>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{label}</label>
              <input
                type={type} name={name} value={value} placeholder={placeholder}
                onChange={onChangeFormData}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-border bg-muted/30">
          <button type="button" onClick={onClickCancel}
            className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent transition-colors">
            Cancel
          </button>
          <button type="submit"
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 transition-colors">
            Add Store
          </button>
        </div>
      </form>
    </div>,
    document.getElementById('addStore')!
  );
};
